import { Alert, Box, Header, Link, SpaceBetween, TextContent } from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import constants from '../../common/constants';
import Breadcrumbs from '../common/Breadcrumbs';
import * as UtilsApi from '../../api/UtilsApi';
import '../../resources/styles/scss/components/apgl-user-profile/apgl-user-profile-form.css';
import AppContextProps from '../../contexts/AppContextProps';
import { withAppContext } from '../../contexts/AppContext';
import {
  ExpertiseItem,
  FormErrorType,
  OtherPublicationItem,
  PublicationItem,
} from './APGLUserProfileUtils';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import {
  APGLUserProfileAttributes,
  APGLUserProfileRecordSubType,
  AWSService,
  AWSServiceExperience,
  ContentWorkflow,
  ExternalPublicationLink,
  Publication,
  TechDomain,
  TechDomainExperience,
} from '@amzn/awscat-pattern-library-service-model';
import APGLUserProfileCreateControls from './APGLUserProfileCreateControls';
import BrowserHistory from '../utils/BrowserHistory';
import LoadingPage from '../common/LoadingPage';
import APGLUserProfileFormBody from './APGLUserProfileFormBody';

let mplServiceClient: IMplServiceClient;
let publicationNameMap: Map<string, string>;
let reviewNameMap: Map<string, string>;

const APGLUserProfileCreatingForm: React.FunctionComponent<AppContextProps> = ({
  appContext,
}: AppContextProps) => {
  const { authnInfo } = appContext;
  // @ts-expect-error TS2339 - strict migration
  const { userProfile } = authnInfo;
  const { userId } = userProfile;

  /* states */

  // duplicated profile check
  const [registered, setRegistered] = useState<boolean>(false);
  const [loadingUserInfo, setLoadingUserInfo] = useState<boolean>(true);

  // description states
  const [description, setDescription] = useState<string>('');

  // peer reviewer option states
  const [isPeerReviewer, setIsPeerReviewer] = useState<boolean>(true);

  // expertise states
  const [techDomainExperiences, setTechDomainExperiences] = useState<TechDomainExperience[]>([]);
  const [awsServiceExperiences, setAwsServiceExperiences] = useState<AWSServiceExperience[]>([]);

  // authored content states
  const [loadingPubs, setLoadingPubs] = useState<boolean>(true);
  const [allPubs, setAllPubs] = useState<string[]>([]);
  const [authoredPubs, setAuthoredPubs] = useState<string[]>([]); // selected authored publications

  // reviewed content states
  const [loadingReviews, setLoadingReviews] = useState<boolean>(true);
  const [allReviews, setAllReviews] = useState<string[]>([]);
  const [reviewedPubs, setReviewedPubs] = useState<string[]>([]); // selected reviewed publications

  // other publication states
  const [otherPubs, setOtherPubs] = useState<ExternalPublicationLink[]>([]);

  // validation error states
  const [formError, setFormError] = useState<Map<FormErrorType, string>>(
    new Map<FormErrorType, string>([
      [FormErrorType.DescriptionError, 'Description field cannot be empty.'],
    ]),
  );
  const [errorVisible, setErrorVisible] = useState<boolean>(false);

  // create profile states
  const [isCreatingProfile, setIsCreatingProfile] = useState<boolean>(false);
  const [createProfileError, setCreateProfileError] = useState<string>('');

  /* initialize */
  useEffect(() => {
    mplServiceClient = MplServiceClientFactory.create();
    publicationNameMap = new Map<string, string>();
    reviewNameMap = new Map<string, string>();

    async function loadRegisteredAPGLUsers() {
      setLoadingUserInfo(true);
      const { apglUserProfile } = await mplServiceClient.getAPGLUserProfile(
        userId,
        APGLUserProfileRecordSubType.BasicInfo,
      );
      const isRegistered = !!apglUserProfile.userId;
      setRegistered(isRegistered);
      setLoadingUserInfo(false);
    }

    async function loadAllPublicationsAndReviews() {
      const { publications: allAPGLPublications } =
        await mplServiceClient.listPublicationsThroughS3();
      await loadPublications(allAPGLPublications);
      await loadReviews(allAPGLPublications);
    }

    async function loadPublications(allAPGLPublications: Publication[]) {
      setLoadingPubs(true);
      // get authored workflows
      const { workflows: authorWorkflows } = await mplServiceClient.getContentsForAuthor(userId);
      const { workflows: coAuthorWorkflows } =
        await mplServiceClient.listSharedContentsForUser(userId);
      let pubId = -1;
      // filter published contents
      const publishedWorkflows: ContentWorkflow[] = [
        ...authorWorkflows,
        ...coAuthorWorkflows,
      ].filter((wf) => UtilsApi.isPublishedWorkflow(wf));
      // build publication items
      const pubItems: PublicationItem[] = publishedWorkflows.map((wf) =>
        Object.assign({ contentId: wf.contentId, id: ++pubId }),
      );
      // populate content ID -> name map
      pubItems.map((item) => {
        const contentName = allAPGLPublications?.find((pub) => pub.contentId === item.contentId)
          ?.contentMetadata?.contentName;
        if (contentName) {
          publicationNameMap.set(item.contentId, contentName); // set content name map
        }
      });
      setAllPubs(pubItems.map((item) => item.contentId));
      setAuthoredPubs(pubItems.map((item) => item.contentId).slice(0, UtilsApi.publicationLimit));
      setLoadingPubs(false);
    }

    async function loadReviews(allAPGLPublications: Publication[]) {
      setLoadingReviews(true);
      // list all contents reviewed by user, including those not published
      const { contentIds } = await mplServiceClient.listContentsReviewedByUser(userId);
      const reviewItems: PublicationItem[] = []; // published contents reviewed by user
      let revId = -1;
      contentIds.map(async (cid) => {
        const publication = allAPGLPublications?.find((pub) => pub.contentId === cid);
        const contentName = publication?.contentMetadata?.contentName;
        const publicationState = publication?.publicationState;
        // only selectable if content is published
        if (
          contentName &&
          publicationState &&
          UtilsApi.isPublishedWorkflowState(publicationState)
        ) {
          const reviewItem: PublicationItem = { contentId: cid, id: ++revId };
          reviewItems.push(reviewItem);
          reviewNameMap.set(cid, contentName);
        }
      });
      setAllReviews(reviewItems.map((item) => item.contentId));
      setReviewedPubs(
        reviewItems.map((item) => item.contentId).slice(0, UtilsApi.publicationLimit),
      );
      setLoadingReviews(false);
    }
    loadRegisteredAPGLUsers();
    loadAllPublicationsAndReviews();
  }, [userId]);

  /* breadcrumb */
  const breadcrumbItems = [
    { text: constants.APP_NAME_BREAD_CRUMB, href: '/' },
    { text: 'Create your APGL user profile', href: '' },
  ];

  /* header */
  const header = <Header variant="h2">User profile</Header>;

  /* already registered warning */
  const alreadyRegisteredWarning = registered ? (
    <Alert type="warning" header={'You have already registered your APGL user profile.'}>
      <span>Please click </span>
      <span>
        <Link href="/user-profile">here</Link> to edit your existing profile or{' '}
      </span>
      <span>
        <Link href="/">go back</Link> to home page.
      </span>
    </Alert>
  ) : null;

  /* form body */

  const onDescriptionChanged = (text: string) => {
    setDescription(text);
    setErrorVisible(false);
  };

  const onPeerReviewerOptionChanged = (checked: boolean) => {
    setIsPeerReviewer(checked);
  };

  const onTechDomainsChanged = (items: ExpertiseItem[]) => {
    const experiences = items.map((item) => {
      const { domainExpertise, yearsOfExperience, rank } = item;
      return TechDomainExperience.create(domainExpertise as TechDomain, yearsOfExperience, rank);
    });
    setTechDomainExperiences(experiences);
    setErrorVisible(false);
  };

  const onAWSServicesChanged = (items: ExpertiseItem[]) => {
    const experiences = items.map((item) => {
      const { domainExpertise, yearsOfExperience, rank } = item;
      return AWSServiceExperience.create(domainExpertise as AWSService, yearsOfExperience, rank);
    });
    setAwsServiceExperiences(experiences);
    setErrorVisible(false);
  };

  const onAuthoredPubsChanged = (pubs: PublicationItem[]) => {
    const pubIds = pubs.map((pub) => pub.contentId);
    setAuthoredPubs(pubIds);
    setErrorVisible(false);
  };

  const onReviewedPubsChanged = (pubs: PublicationItem[]) => {
    const pubIds = pubs.map((pub) => pub.contentId);
    setReviewedPubs(pubIds);
    setErrorVisible(false);
  };

  const onOtherPubsChanged = (pubs: OtherPublicationItem[]) => {
    const pubLinks = pubs.map((pub) => ExternalPublicationLink.create(pub.title, pub.url));
    setOtherPubs(pubLinks);
    setErrorVisible(false);
  };

  const onFormErrorChanged = (error: Map<FormErrorType, string>) => {
    setFormError(error);
  };

  const formBody = !registered ? (
    <APGLUserProfileFormBody
      publicationNameMap={publicationNameMap}
      reviewNameMap={reviewNameMap}
      upDescription={description}
      upIsPeerReviewer={isPeerReviewer}
      upTechDomains={techDomainExperiences}
      upAwsServices={awsServiceExperiences}
      apgPublications={authoredPubs}
      reviewedPublications={reviewedPubs}
      otherPublications={otherPubs}
      allPubs={allPubs}
      allReviews={allReviews}
      appContext={appContext}
      handleDescriptionChanged={onDescriptionChanged}
      handlePeerReviewerOptionChanged={onPeerReviewerOptionChanged}
      handleTechDomainsChanged={onTechDomainsChanged}
      handleAWSServicesChanged={onAWSServicesChanged}
      handleAuthoredPubsChanged={onAuthoredPubsChanged}
      handleReviewedPubsChanged={onReviewedPubsChanged}
      handleOtherPubsChanged={onOtherPubsChanged}
      handleFormErrorChanged={onFormErrorChanged}
      initFormError={formError}
    />
  ) : null;

  const hasFormError = () => {
    return formError.size > 0;
  };

  const onConfirmRegistration = async () => {
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
    if (hasFormError()) {
      setErrorVisible(true);
      return;
    }
    setIsCreatingProfile(true);
    try {
      await mplServiceClient.createAPGLUserProfile(
        description,
        APGLUserProfileAttributes.create({
          isPeerReviewer,
          techDomains: techDomainExperiences.filter((exp) => exp.techDomain),
          awsServices: awsServiceExperiences.filter((exp) => exp.awsService),
          apgPublications: authoredPubs.filter((pub) => pub),
          reviewedPublications: reviewedPubs.filter((pub) => pub),
          otherPublications: otherPubs.filter((pub) => pub.title),
        }),
      );
      BrowserHistory.push('/user-profile');
    } catch (e) {
      // tslint:disable-next-line: no-console
      console.error(e);
      // @ts-expect-error TS18046 - strict migration
      setCreateProfileError(e.message);
    } finally {
      setIsCreatingProfile(false);
    }
  };

  const onCancelRegistration = () => {
    BrowserHistory.push(`/`);
  };

  const generateFormError = () => {
    const errors = Array.from(formError.values());
    // @ts-expect-error TS2322 - strict migration
    let error: React.ReactElement =
      errorVisible && hasFormError() ? (
        <Alert
          type="error"
          header="The following errors exist in your user profile registration form:"
        >
          <TextContent>
            <ul>
              {errors.map((errorText, idx) => (
                <li key={errorText}>{errorText}</li>
              ))}
            </ul>
            <p>Please fix these errors before confirming user profile registration.</p>
          </TextContent>
        </Alert>
      ) : null;
    if (createProfileError) {
      error = <Alert type="error" header={constants.CREATE_APGL_USER_PROFILE_ERROR} />;
    }
    return error;
  };

  const controlsSection = registered ? null : (
    <Box float="right">
      <APGLUserProfileCreateControls
        isCreatingProfile={isCreatingProfile}
        handleConfirm={onConfirmRegistration}
        handleCancel={onCancelRegistration}
      />
    </Box>
  );

  return loadingUserInfo || loadingPubs || loadingReviews ? (
    <LoadingPage />
  ) : (
    <div id="user-profile-registration">
      <Box margin={{ left: 'xxl', top: 'm', bottom: 'xxl', right: 'xxl' }}>
        <Breadcrumbs items={breadcrumbItems} />
        <Box margin={{ left: 'l', top: 'm', bottom: 'l', right: 'l' }}>
          <SpaceBetween direction="vertical" size="xl">
            {header}
            {alreadyRegisteredWarning}
            {generateFormError()}
            {formBody}
            {controlsSection}
          </SpaceBetween>
        </Box>
      </Box>
    </div>
  );
};

export default withAppContext(APGLUserProfileCreatingForm);
